import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'
import AssetForm from './AssetForm'
import AssetDetail from './AssetDetail'
import AssetMap from './AssetMap'

interface Asset {
  id: number
  name: string
  type: 'livestock' | 'wild_animal' | 'crop_field' | 'building'
  description: string | null
  site_id: number
  site_name: string
  area_id: number | null
  area_name: string | null
  created_by: number
  created_by_name: string
  created_by_fullname: string
  comment: string | null
  time_started: string
  time_ended: string | null
  position_type: 'point' | 'polygon'
  position_coords: [number, number] | [number, number][]
  is_active: boolean
  status: 'active' | 'completed'
  duration: number
  created_at: string
  updated_at: string
}

interface AssetType {
  name: string
  icon: string
  color: string
  defaultPositionType: 'point' | 'polygon'
}

interface AssetDashboardProps {
  user: User
}

function AssetDashboard({ user }: AssetDashboardProps) {
  const [assets, setAssets] = useState<Asset[]>([])
  const [assetTypes, setAssetTypes] = useState<Record<string, AssetType>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [showAssetForm, setShowAssetForm] = useState(false)
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null)
  const [editingAssetId, setEditingAssetId] = useState<number | null>(null)
  const [showMapView, setShowMapView] = useState(false)

  useEffect(() => {
    fetchAssetTypes()
    fetchAssets()
  }, [statusFilter, typeFilter])

  const fetchAssetTypes = async () => {
    try {
      const response = await fetchWithAuth('/api/assets/types')
      if (response.ok) {
        const types = await response.json()
        setAssetTypes(types)
      }
    } catch (err) {
      console.error('Failed to fetch asset types:', err)
    }
  }

  const fetchAssets = async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (typeFilter) params.append('type', typeFilter)

      const response = await fetchWithAuth(`/api/assets?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch assets')
      }
      const data = await response.json()
      setAssets(data.assets)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatDuration = (days: number) => {
    if (days === 0) return 'Today'
    if (days === 1) return '1 day'
    return `${days} days`
  }

  const getAssetTypeInfo = (type: string) => {
    return assetTypes[type] || { name: type, icon: '📦', color: '#666' }
  }

  if (loading) {
    return <div className="loading">Loading assets...</div>
  }

  return (
    <div className="asset-dashboard">
      <div style={{ marginBottom: '1rem' }}>
        <button 
          className="btn" 
          onClick={() => window.location.href = `/app${window.location.search}`}
        >
          ← Back to User View
        </button>
      </div>

      <div className="dashboard-header">
        <h2>📦 Asset Dashboard</h2>
        <div className="dashboard-actions">
          <button 
            className="btn btn-success"
            onClick={() => setShowAssetForm(true)}
          >
            ➕ New Asset
          </button>
          <button 
            className="btn"
            onClick={() => setShowMapView(true)}
          >
            🗺️ Map View
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Filters */}
      <div className="filters-container">
        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(assetTypes).map(([key, type]) => (
              <option key={key} value={key}>
                {type.icon} {type.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Asset Statistics */}
      <div className="task-stats">
        <div className="stat-card stat-active">
          <div className="stat-number">{assets.filter(a => a.status === 'active').length}</div>
          <div className="stat-label">Active Assets</div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-number">{assets.filter(a => a.status === 'completed').length}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-number">{Object.keys(assetTypes).map(type => assets.filter(a => a.type === type).length).reduce((a, b) => Math.max(a, b), 0)}</div>
          <div className="stat-label">Most Common Type</div>
        </div>
        <div className="stat-card stat-overdue">
          <div className="stat-number">{assets.filter(a => a.duration > 365).length}</div>
          <div className="stat-label">Long-term (&gt;1 year)</div>
        </div>
      </div>

      {/* Assets Table */}
      <div className="table-container">
        {assets.length === 0 ? (
          <div className="loading">No assets found</div>
        ) : (
          <table className="table tasks-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Type</th>
                <th>Status</th>
                <th>Location</th>
                <th>Duration</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => {
                const typeInfo = getAssetTypeInfo(asset.type)
                return (
                  <tr key={asset.id} className="task-row">
                    <td>
                      <div className="task-title">{asset.name}</div>
                      {asset.description && (
                        <div className="task-description">{asset.description}</div>
                      )}
                      {asset.comment && (
                        <div className="task-comments">💬 {asset.comment}</div>
                      )}
                    </td>
                    <td>
                      <span 
                        className="priority-badge" 
                        style={{ backgroundColor: typeInfo.color }}
                      >
                        {typeInfo.icon} {typeInfo.name}
                      </span>
                    </td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{ 
                          backgroundColor: asset.status === 'active' ? '#27ae60' : '#95a5a6' 
                        }}
                      >
                        {asset.status}
                      </span>
                    </td>
                    <td>
                      <div className="location-info">
                        <div className="site-name">🏠 {asset.site_name}</div>
                        {asset.area_name && (
                          <div className="area-name">📍 {asset.area_name}</div>
                        )}
                        <div className="position-type">
                          {asset.position_type === 'point' ? '📍 Point' : '🟦 Area'}
                        </div>
                      </div>
                    </td>
                    <td className="date-cell">
                      <div>{formatDuration(asset.duration)}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {formatDate(asset.time_started)}
                        {asset.time_ended && (
                          <> → {formatDate(asset.time_ended)}</>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>{asset.created_by_fullname || asset.created_by_name}</div>
                    </td>
                    <td>
                      <div className="task-actions">
                        <button 
                          className="btn btn-sm"
                          onClick={() => setSelectedAssetId(asset.id)}
                        >
                          View
                        </button>
                        {(user.isAdmin || user.id === asset.created_by) && (
                          <button 
                            className="btn btn-sm btn-edit"
                            onClick={() => setEditingAssetId(asset.id)}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAssetForm && (
        <AssetForm
          assetTypes={assetTypes}
          onSubmit={() => {
            setShowAssetForm(false)
            fetchAssets()
          }}
          onCancel={() => setShowAssetForm(false)}
        />
      )}

      {editingAssetId && (
        <AssetForm
          assetTypes={assetTypes}
          isEditing={true}
          assetId={editingAssetId}
          initialData={assets.find(a => a.id === editingAssetId)}
          onSubmit={() => {
            setEditingAssetId(null)
            fetchAssets()
          }}
          onCancel={() => setEditingAssetId(null)}
        />
      )}

      {selectedAssetId && (
        <AssetDetail
          assetId={selectedAssetId}
          user={user}
          assetTypes={assetTypes}
          onClose={() => setSelectedAssetId(null)}
          onUpdate={() => fetchAssets()}
        />
      )}

      {showMapView && (
        <AssetMap
          assetTypes={assetTypes}
          onClose={() => setShowMapView(false)}
          onAssetSelect={(assetId) => {
            setSelectedAssetId(assetId)
            setShowMapView(false)
          }}
        />
      )}
    </div>
  )
}

export default AssetDashboard